import { View, Text, StyleSheet } from 'react-native'
import { mesaColorNames, fontFamilies } from '@/src/common/theme'
import { provideCardPlatform } from '@/src/experiences/card-platform/provideCardPlatform'
import type { FunctionComponent, PropsWithChildren } from 'react'
import { LegacyBalance } from '@/src/experiences/card-payments/screens/MakePayment/Balance/LegacyBalance'
import { PlatformBalance } from '@/src/experiences/card-payments/screens/MakePayment/Balance/PlatformBalance'
import { Separator, useEvent } from 'tamagui'
import { Skeleton } from '@/src/design-system/Skeleton'

export type BalanceComponentType = FunctionComponent<{
  currentBalance?: { value: number; formatted: string }
  statementBalance?: { value: number; formatted: string }
  minimumPayment?: { value?: string; formatted: string }
  statementDueOn?: { value?: Date; formatted: PropsWithChildren['children'] }
  totalCreditLimit?: string
  paymentCardNextClosingDate?: PropsWithChildren['children']
  availableCredit?: { value: number; formatted: string }
  paymentCardCreditLine?: { value: number; formatted: string }
  purchaseAPR?: number
  refetchCardAccountSummary: () => void
}>

export const withBalance = provideCardPlatform<BalanceComponentType>(
  ({ ToWrap, platform, newestApplicationQuery }) => {
    const refetchNewestApplication = useEvent(() => void newestApplicationQuery.refetch())
    switch (platform) {
      case 'HIGHNOTE':
        return <LegacyBalance BalanceComponent={ToWrap} />
      case 'LOANPRO':
        return <PlatformBalance BalanceComponent={ToWrap} />
      default:
        platform satisfies undefined
        return <ToWrap refetchCardAccountSummary={refetchNewestApplication} />
    }
  },
)

const balanceDetailSkeleton = <Skeleton style={{ height: 17.5, width: 88 }} />

export const Balance = withBalance(
  ({
    currentBalance,
    statementBalance,
    minimumPayment,
    statementDueOn,
    paymentCardNextClosingDate,
    availableCredit,
    paymentCardCreditLine,
    purchaseAPR,
  }) => (
    <View style={styles.balanceContainer}>
      <View style={{ rowGap: 5, marginBottom: 10 }}>
        <Text style={styles.currentBalanceTitle}>Current Balance</Text>
        {typeof currentBalance?.formatted === 'string' ? (
          <Text style={styles.currentBalanceValue}>{currentBalance.formatted || 'N/A'}</Text>
        ) : (
          <Skeleton style={{ height: 40, width: 222 }} skeletonProps={{ height: 40, width: 222 }} />
        )}
      </View>
      {[
        {
          name: 'Statement Balance',
          value:
            typeof statementBalance?.formatted === 'string'
              ? statementBalance.formatted || 'N/A'
              : balanceDetailSkeleton,
        },
        {
          name: 'Minimum Payment Due',
          value:
            typeof minimumPayment?.formatted === 'string'
              ? minimumPayment.formatted || 'N/A'
              : balanceDetailSkeleton,
        },
        {
          name: 'Due Date',
          value: statementDueOn?.formatted ?? balanceDetailSkeleton,
        },
        {
          name: 'Statement Close Date',
          value: paymentCardNextClosingDate ?? balanceDetailSkeleton,
          bottomSeparator: true,
        },
        undefined,
        {
          name: 'Available Credit Limit',
          value:
            typeof availableCredit?.formatted === 'string'
              ? availableCredit.formatted || 'N/A'
              : balanceDetailSkeleton,
        },
        {
          name: 'Total Credit Limit',
          value:
            typeof paymentCardCreditLine?.formatted === 'string'
              ? paymentCardCreditLine.formatted || 'N/A'
              : balanceDetailSkeleton,
        },
        {
          name: 'Purchase APR',
          value: typeof purchaseAPR === 'number' ? `${purchaseAPR}%` : balanceDetailSkeleton,
        },
      ].map((paymentDetail) =>
        paymentDetail ? (
          <View style={styles.balanceInformation} key={paymentDetail.name}>
            <Text style={styles.infoTitle}>{paymentDetail.name}</Text>
            <Text style={styles.infoValues}>{paymentDetail.value}</Text>
          </View>
        ) : (
          <Separator
            key="separator"
            style={{
              borderColor: mesaColorNames['global/grey/20 #E6E9EB'],
              marginTop: 16,
              marginBottom: 16,
            }}
          />
        ),
      )}
    </View>
  ),
)

const styles = StyleSheet.create({
  balanceContainer: {
    backgroundColor: mesaColorNames['Mesa - Brand/White #FFFFFF'],
    borderRadius: 8,
    padding: 16,
  },
  currentBalanceTitle: {
    fontSize: 14,
    fontFamily: fontFamilies['PlusJakartaSans-Bold'],
    fontWeight: 700,
  },
  currentBalanceValue: {
    fontSize: 32,
    fontFamily: fontFamilies['PlusJakartaSans-Bold'],
    fontWeight: 700,
  },
  balanceInformation: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginVertical: 5,
  },
  infoTitle: {
    fontSize: 14,
    fontFamily: fontFamilies['PlusJakartaSans-Regular'],
    fontWeight: 400,
    color: mesaColorNames['Greyscale/500 #6E7375'],
  },
  infoValues: {
    fontSize: 14,
    fontFamily: fontFamilies['PlusJakartaSans-Bold'],
    fontWeight: 700,
    color: mesaColorNames['global/grey/80 #2F3133'],
    display: 'flex',
  },
})
